import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import httpInstances from '@/api/axiosInstance';
import { getInternalBasePath } from '@/utils/environmentPaths';
import { extractErrorMessage } from '@/utils/extractErrorMessage';
import { handleThunkError } from '@/utils/thunkErrorHandler';

type MarketsListState = {
  status: 'idle' | 'loading' | 'failed' | 'fulfilled';
  error: string | null;
  marketsList: MarketsListItem[];
  groupedMarkets: MarketGroup[];
};

const initialState: MarketsListState = {
  status: 'idle',
  error: null,
  marketsList: [],
  groupedMarkets: []
};

export interface MarketsListItem {
  marketId: string;
  metadata: {
    displayName: string;
    commodity: string;
    region: string;
  };
}

export type MarketGroup = {
  name: string;
  order: number;
  markets: { name: string; comingSoon?: boolean; tag?: string | null; marketId?: string }[];
};

interface MarketDTO {
  insightId: string;
  metadata: {
    displayName: string;
    commodity: string;
    region: string;
    tag?: string | null;
    comingSoon?: boolean;
  };
}

interface MarketGroupDTO {
  subHeading: string;
  order: number;
  markets: MarketDTO[];
}

export const getMarketsList = createAsyncThunk(
  'getMarketsList',
  async (verificationToken: string | undefined, { rejectWithValue }) => {
    try {
      const { data } = await httpInstances.axiosInternalInstance.get<MarketGroupDTO[]>(
        `${getInternalBasePath()}markets`,
        verificationToken
          ? { headers: { Authorization: `Bearer ${verificationToken}` } }
          : undefined
      );

      return {
        marketsList: data.reduce((acc, item) => {
          const { markets } = item;
          return [
            ...acc,
            ...markets.map(market => ({
              marketId: market.insightId.toLowerCase(),
              metadata: {
                displayName: market.metadata.displayName,
                commodity: market.metadata.commodity,
                region: market.metadata.region
              }
            }))
          ];
        }, [] as MarketsListItem[]),
        groupedMarkets: data.map(item => {
          const { subHeading, order, markets } = item;
          return {
            name: subHeading,
            order,
            markets: markets.map(market => ({
              name: market.metadata.displayName,
              tag: market.metadata.tag,
              comingSoon: market.metadata.comingSoon,
              marketId: market.insightId.toLowerCase()
            }))
          } as MarketGroup;
        })
      };
    } catch (error) {
      return handleThunkError(error, rejectWithValue) as never;
    }
  }
);

const marketsListSlice = createSlice({
  name: 'marketsList',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getMarketsList.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(getMarketsList.fulfilled, (state, action) => {
        state.status = 'fulfilled';

        state.marketsList = action.payload.marketsList;
        state.groupedMarkets = action.payload.groupedMarkets;
      })
      .addCase(getMarketsList.rejected, (state, action) => {
        state.status = 'failed';
        state.error = extractErrorMessage(action.payload, action.error.message);
      });
  }
});

export default marketsListSlice.reducer;
